/**
 * Історія браузера для роутера, сумісна з кешем «назад / вперед» (bfcache, UX-34).
 *
 * `createBrowserHistory` з `@tanstack/history` вішає слухач `beforeunload` на весь час життя
 * сторінки, навіть коли блокувальників немає. Firefox сторінку з таким слухачем у bfcache не
 * кладе: «Назад» з іншого сайту чи з документа входу перезавантажує застосунок замість миттєвого
 * повернення. Тому історії підставляємо обгортку `window`: слухач `beforeunload` вона запам'ятовує,
 * а вішає лише поки є блокувальник, якому він потрібен (`enableBeforeUnload` не `false`). Наші
 * блокувальники (`useUnsavedGuard`) ставлять `enableBeforeUnload: false` і свій слухач лише на
 * час незбережених змін, тож у звичайній роботі слухача немає зовсім.
 *
 * Решта властивостей `window` — без змін (Proxy), тож оновлення бібліотеки, що читає інші поля,
 * нічого не ламає. Тест — history.test.ts.
 */
import { createBrowserHistory, type RouterHistory } from '@tanstack/react-router';

type Blocker = Parameters<RouterHistory['block']>[0];
type Listener = EventListenerOrEventListenerObject;

const BEFORE_UNLOAD = 'beforeunload';

export function createBfcacheHistory(win: Window = window): RouterHistory {
  let held: { listener: Listener; options?: boolean | AddEventListenerOptions } | null = null;
  let attached = false;
  const wanting = new Set<Blocker>();

  const sync = () => {
    // Без запам'ятованого слухача `attached` завжди false (його скидає `remove`).
    if (!held) return;
    const want = wanting.size > 0;
    if (want === attached) return;
    if (want) win.addEventListener(BEFORE_UNLOAD, held.listener, held.options);
    else win.removeEventListener(BEFORE_UNLOAD, held.listener, held.options);
    attached = want;
  };

  const add = (type: string, listener: Listener, options?: boolean | AddEventListenerOptions) => {
    if (type !== BEFORE_UNLOAD) return win.addEventListener(type, listener, options);
    held = { listener, ...(options === undefined ? {} : { options }) };
    sync();
  };
  const remove = (type: string, listener: Listener, options?: boolean | EventListenerOptions) => {
    if (type !== BEFORE_UNLOAD) return win.removeEventListener(type, listener, options);
    if (held?.listener !== listener) return;
    if (attached) win.removeEventListener(BEFORE_UNLOAD, listener, held.options);
    held = null;
    attached = false;
  };

  const shim = new Proxy(win, {
    get(target, prop) {
      if (prop === 'addEventListener') return add;
      if (prop === 'removeEventListener') return remove;
      const value: unknown = Reflect.get(target, prop, target);
      return typeof value === 'function' ? value.bind(target) : value;
    },
  });

  const history = createBrowserHistory({ window: shim });
  const block = history.block;
  history.block = (blocker) => {
    const unblock = block(blocker);
    const needsListener = blocker.enableBeforeUnload !== false;
    if (needsListener) {
      wanting.add(blocker);
      sync();
    }
    return () => {
      unblock();
      if (needsListener) {
        wanting.delete(blocker);
        sync();
      }
    };
  };
  return history;
}
